import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Native dialogs share the browser's top layer. No page transforms, increasing
// z-index values or document-wide Escape handlers are needed for nested dialogs.
const openDialogs = new Set();
let bodyStyle = null;

function lockScroll(dialog) {
  if (!openDialogs.size) {
    bodyStyle = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight };
    const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    if (gap) document.body.style.paddingRight = `${parseFloat(getComputedStyle(document.body).paddingRight || '0') + gap}px`;
    document.body.style.overflow = 'hidden';
  }
  openDialogs.add(dialog);
}

function unlockScroll(dialog) {
  openDialogs.delete(dialog);
  if (!openDialogs.size && bodyStyle) {
    document.body.style.overflow = bodyStyle.overflow;
    document.body.style.paddingRight = bodyStyle.paddingRight;
    bodyStyle = null;
  }
}

function DialogWindow({ children, onClose, onEscape, closeDisabled = false,
  label, labelledBy, describedBy, size = 'md', className = '', initialFocusRef,
  role = 'dialog', ...props }) {
  const ref = useRef(null);
  const backdropStart = useRef(false);
  useLayoutEffect(() => {
    const dialog = ref.current;
    const previousFocus = document.activeElement;
    dialog.showModal();
    lockScroll(dialog);
    // Focus the heading/panel by default, avoiding unexpected mobile keyboards.
    (initialFocusRef?.current || dialog.querySelector('[data-dialog-heading]') || dialog).focus({ preventScroll: true });
    return () => {
      dialog.close();
      unlockScroll(dialog);
      if (previousFocus?.isConnected && !previousFocus.closest('[inert]')) {
        const parent = previousFocus.closest('dialog');
        if (!parent || parent.open) previousFocus.focus({ preventScroll: true });
      }
    };
  // A changing inline callback must not close/reopen a dialog or erase focus.
  }, []);

  const outside = event => {
    if (event.target !== ref.current) return false;
    const rect = ref.current.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  };
  return createPortal(
    <dialog {...props} ref={ref} className={`ap-dialog ap-dialog--${size} ${className}`}
      role={role} aria-modal="true" aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy} aria-describedby={describedBy} tabIndex={-1}
      onCancel={event => {
        event.preventDefault();
        if (!closeDisabled) (onEscape || onClose)?.();
      }}
      onPointerDown={event => { backdropStart.current = outside(event); }}
      onClick={event => {
        if (backdropStart.current && outside(event) && !closeDisabled) onClose?.();
        backdropStart.current = false;
      }}>
      {children}
    </dialog>, document.body,
  );
}

export default function Dialog({ open = true, ...props }) {
  if (!open || typeof document === 'undefined') return null;
  return <DialogWindow {...props} />;
}
